import {
    useCallback,
    useEffect,
    useState
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

export interface ModalConfig {
    isOpen: boolean;
    activeTab?: string;
    onClose?: () => void;
    data?: any;
}

export interface ModalState {
    modals: Record<string, ModalConfig>;
    openModal: (modalId: string, config?: Partial<ModalConfig>) => void;
    closeModal: (modalId: string) => void;
    toggleModal: (modalId: string, config?: Partial<ModalConfig>) => void;
    updateModal: (modalId: string, config: Partial<ModalConfig>) => void;
    isModalOpen: (modalId: string) => boolean;
    getModalConfig: (modalId: string) => ModalConfig;
}

export const modalInitialState: ModalState = {
    modals: {},
    openModal: () => {},
    closeModal: () => {},
    toggleModal: () => {},
    updateModal: () => {},
    isModalOpen: () => false,
    getModalConfig: () => ({ isOpen: false }),
};

export const modalInitialModalState: ModalState = modalInitialState;

export const createModalProvider = () => {
    const [modals, setModals] = useState<Record<string, ModalConfig>>({});

    const openModal = useCallback((modalId: string, config?: Partial<ModalConfig>) => {
        setModals(prev => ({
            ...prev,
            [modalId]: {
                isOpen: true,
                activeTab: config?.activeTab,
                onClose: config?.onClose,
                data: config?.data,
                ...config,
            },
        }));
    }, [setModals]);

    const closeModal = useCallback((modalId: string) => {
        setModals(prev => {
            const modal = prev[modalId];
            if (modal?.onClose) {
                modal.onClose();
            }
            return {
                ...prev,
                [modalId]: {
                    ...modal,
                    isOpen: false,
                },
            };
        });
    }, [setModals]);

    const toggleModal = useCallback((modalId: string, config?: Partial<ModalConfig>) => {
        setModals(prev => {
            const modal = prev[modalId] || { isOpen: false };
            const newState = !modal.isOpen;
            return {
                ...prev,
                [modalId]: {
                    ...modal,
                    ...config,
                    isOpen: newState,
                },
            };
        });
    }, [setModals]);

    const updateModal = useCallback((modalId: string, config: Partial<ModalConfig>) => {
        setModals(prev => {
            const modal = prev[modalId] || { isOpen: false };
            return {
                ...prev,
                [modalId]: {
                    ...modal,
                    ...config,
                },
            };
        });
    }, [setModals]);

    const isModalOpen = useCallback((modalId: string) => {
        const modal = modals[modalId];
        return modal?.isOpen || false;
    }, [modals]);

    const getModalConfig = useCallback((modalId: string) => {
        return modals[modalId] || { isOpen: false };
    }, [modals]);


    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    useEffect(() => {
        
        const setting_modal = searchParams.get('setting_modal');
        if (setting_modal && openModal) {
            // 打开设置模态框，并设置活动标签
            openModal('profile', {
                activeTab: setting_modal,
                onClose: () => {
                    // 清理 URL 参数
                    navigate({pathname: location.pathname.replace('/admin', '')}, { replace: true });
                }
            });
    
            // 显示成功消息
            setTimeout(() => {
                // 这里需要通过 context 或其他方式来访问 showToast
                console.log('Connected successfully, your Shopify store is now connected.');
            }, 1000);
        }
    }, [searchParams, openModal, navigate]);

    const contextValue: ModalState = {
        modals,
        openModal,
        closeModal,
        toggleModal,
        updateModal,
        isModalOpen,
        getModalConfig,
    };

    return contextValue;
};